// Utility functions for product data
export const productUtils = {
  // Filter products by season
  filterBySeason(products, currentSeason) {
    if (!currentSeason || !products) return products;

    return products.filter((product) => {
      if (!product.seasons) return true;

      const { peak, available, bestMonths } = product.seasons;
      const currentMonth = new Date().getMonth() + 1;

      return (
        peak?.includes(currentSeason) ||
        available?.toLowerCase() === 'year-round' ||
        bestMonths?.includes(currentMonth)
      );
    });
  },

  searchHome(products, query) {
    if (!query || !products || !Array.isArray(products)) {
      return [];
    }

    if (products.length === 0) {
      return [];
    }

    const lowerQuery = query.toLowerCase().trim();

    const results = products.filter((product, index) => {
      if (!product || typeof product !== 'object') {
        return false;
      }

      // Check if product has searchable fields
      const name = product.name || '';
      const description = product.description || '';
      const variety = product.variety || '';
      const category = product.category || '';

      // Search in multiple fields
      const nameMatch = name.toLowerCase().includes(lowerQuery);
      const descMatch = description.toLowerCase().includes(lowerQuery);
      const varietyMatch = variety.toLowerCase().includes(lowerQuery);
      const categoryMatch = category.toLowerCase().includes(lowerQuery);

      const isMatch = nameMatch || descMatch || varietyMatch || categoryMatch;

      return isMatch;
    });

    return results;
  },

  // Search products by name or description
  searchProducts(products, query) {
    if (!query || !products) return products;

    const lowerQuery = query.toLowerCase();
    return products.filter(
      (product) =>
        product.name?.toLowerCase().includes(lowerQuery) ||
        product.description?.toLowerCase().includes(lowerQuery) ||
        product.variety?.toLowerCase().includes(lowerQuery)
    );
  },

  // Sort products by various criteria
  sortProducts(products, sortBy = 'name') {
    if (!products) return products;

    return [...products].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name?.localeCompare(b.name);

        case 'season':
          return this.getSeasonPriority(a) - this.getSeasonPriority(b);

        case 'peak-season':
          const aIsPeak =
            this.isInSeason(a) && this.getSeasonStatus(a).type === 'best-month';
          const bIsPeak =
            this.isInSeason(b) && this.getSeasonStatus(b).type === 'best-month';
          if (aIsPeak && !bIsPeak) return -1;
          if (!aIsPeak && bIsPeak) return 1;
          return this.getSeasonPriority(a) - this.getSeasonPriority(b);

        case 'year-round':
          const aIsYearRound =
            a.seasons?.available?.toLowerCase() === 'year-round';
          const bIsYearRound =
            b.seasons?.available?.toLowerCase() === 'year-round';
          if (aIsYearRound && !bIsYearRound) return -1;
          if (!aIsYearRound && bIsYearRound) return 1;
          return a.name?.localeCompare(b.name);

        default:
          return 0;
      }
    });
  },

  // Get season priority for sorting
  getSeasonPriority(product) {
    if (!product.seasons) return 999;

    const currentMonth = new Date().getMonth() + 1;
    const { bestMonths, peak } = product.seasons;

    if (bestMonths?.includes(currentMonth)) return 1;
    if (peak?.some((season) => this.isCurrentSeason(season))) return 2;

    return 3;
  },

  isCurrentSeason(season) {
    const currentMonth = new Date().getMonth() + 1;
    const seasonMap = {
      spring: [3, 4, 5],
      summer: [6, 7, 8],
      fall: [9, 10, 11],
      winter: [12, 1, 2],
    };

    return seasonMap[season.toLowerCase()]?.includes(currentMonth) || false;
  },

  // Get featured products with fallback
  getFeaturedProducts(categoryData) {
    return categoryData?.featured || categoryData?.products?.slice(0, 2) || [];
  },

  // Format price for display
  formatPrice(priceRange, format = 'wholesale') {
    if (!priceRange) return 'Price on request';

    const price = priceRange[format] || priceRange.wholesale;
    return price || 'Contact for pricing';
  },

  // if product is available
  isProductAvailable(product) {
    if (!product.seasons) return true;

    const { available, bestMonths } = product.seasons;
    const currentMonth = new Date().getMonth() + 1;

    return (
      available?.toLowerCase() === 'year-round' ||
      bestMonths?.includes(currentMonth)
    );
  },

  isInSeason(product) {
    if (!product?.seasons) return true;

    const { peak, available, bestMonths } = product.seasons;
    const currentMonth = new Date().getMonth() + 1;

    // Check if available year-round
    if (available?.toLowerCase() === 'year-round') {
      return true;
    }

    if (bestMonths && Array.isArray(bestMonths)) {
      if (bestMonths.includes(currentMonth)) {
        return true;
      }
    }

    // Check peak seasons
    if (peak && Array.isArray(peak)) {
      if (peak.some((season) => this.isCurrentSeason(season))) {
        return true;
      }
    }

    // Check available range
    if (available && typeof available === 'string' && available.includes('-')) {
      return this.isInAvailableRange(available);
    }

    return false;
  },

  // Get current season based on month
  getCurrentSeason() {
    const currentMonth = new Date().getMonth() + 1;

    if (currentMonth >= 3 && currentMonth <= 5) return 'Spring';
    if (currentMonth >= 6 && currentMonth <= 8) return 'Summer';
    if (currentMonth >= 9 && currentMonth <= 11) return 'Fall';
    return 'Winter';
  },

  // Check if a season matches current season
  isCurrentSeason(season) {
    const currentSeason = this.getCurrentSeason();
    return season.toLowerCase() === currentSeason.toLowerCase();
  },

  // Check if current month is within available range (e.g., "August-March")
  isInAvailableRange(availableRange) {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    // Extract just the month range (e.g., "December-April" from "December-April (Northern Africa)")
    let rangePart = availableRange;
    const monthMatch = availableRange.match(/([A-Za-z]+)\s*-\s*([A-Za-z]+)/);
    if (monthMatch) {
      rangePart = `${monthMatch[1]}-${monthMatch[2]}`;
    }

    const [startMonth, endMonth] = rangePart.split('-').map((m) => m.trim());
    const currentMonth = new Date().getMonth();

    const startIndex = monthNames.findIndex((m) =>
      m.toLowerCase().startsWith(startMonth.toLowerCase())
    );
    const endIndex = monthNames.findIndex((m) =>
      m.toLowerCase().startsWith(endMonth.toLowerCase())
    );

    if (startIndex === -1 || endIndex === -1) return false;

    // Handle ranges that cross year boundary (e.g., August-March)
    if (startIndex > endIndex) {
      return currentMonth >= startIndex || currentMonth <= endIndex;
    } else {
      return currentMonth >= startIndex && currentMonth <= endIndex;
    }
  },

  // Get season status with more detailed information
  getSeasonStatus(product) {
    if (!product?.seasons) {
      return { inSeason: true, status: 'Available', type: 'year-round' };
    }

    const { peak, available, bestMonths, harvestMonths } = product.seasons;
    const currentMonth = new Date().getMonth() + 1;
    const currentMonthName = new Date().toLocaleString('default', {
      month: 'long',
    });
    const currentSeason = this.getCurrentSeason();

    // Check for year-round (case insensitive, and check for phrases)
    const isYearRound =
      available &&
      (available.toLowerCase() === 'year-round' ||
        available.toLowerCase().includes('year-round') ||
        (typeof available === 'string' &&
          available.toLowerCase().includes('year round')));

    if (isYearRound) {
      return { inSeason: true, status: 'Year-Round', type: 'year-round' };
    }

    // Check best months - handle both number arrays and string arrays
    if (bestMonths && bestMonths.length > 0) {
      // Convert bestMonths to numbers if they're strings
      const normalizedBestMonths = bestMonths
        .map((month) => {
          if (typeof month === 'number') return month;
          if (typeof month === 'string') {
            const monthNames = [
              'january',
              'february',
              'march',
              'april',
              'may',
              'june',
              'july',
              'august',
              'september',
              'october',
              'november',
              'december',
            ];
            const index = monthNames.findIndex((m) =>
              month.toLowerCase().includes(m)
            );
            return index !== -1 ? index + 1 : null;
          }
          return null;
        })
        .filter((m) => m !== null);

      if (normalizedBestMonths.includes(currentMonth)) {
        return { inSeason: true, status: 'Peak Season', type: 'best-month' };
      }
    }

    // Check peak seasons
    if (
      peak &&
      peak.length > 0 &&
      peak.some((season) => this.isCurrentSeason(season))
    ) {
      return { inSeason: true, status: 'In Season', type: 'peak-season' };
    }

    // Check available range (e.g., "May-January", "March-September", "November-April")
    if (available && typeof available === 'string' && available.includes('-')) {
      if (this.isInAvailableRange(available)) {
        return { inSeason: true, status: 'Available', type: 'available-range' };
      }
    }

    // Check harvest months range
    if (harvestMonths && harvestMonths.length > 0) {
      for (const harvestRange of harvestMonths) {
        if (harvestRange.includes('-')) {
          if (this.isInAvailableRange(harvestRange)) {
            return {
              inSeason: true,
              status: 'In Season',
              type: 'available-range',
            };
          }
        }
      }
    }

    // Not in season - provide next available info
    const nextAvailable = this.getNextAvailableInfo(product);
    return {
      inSeason: false,
      status: 'Out of Season',
      type: 'out-of-season',
      nextAvailable: nextAvailable,
    };
  },

  // Get next available information for out-of-season products
  getNextAvailableInfo(product) {
    if (!product?.seasons) return 'Check availability';

    const { peak, available, bestMonths, harvestMonths } = product.seasons;
    const currentMonth = new Date().getMonth() + 1;

    // Check for year-round
    if (
      available &&
      (available.toLowerCase() === 'year-round' ||
        available.toLowerCase().includes('year-round'))
    ) {
      return 'Available year-round';
    }

    // Find next best month
    if (bestMonths && bestMonths.length > 0) {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];

      // Convert bestMonths to month numbers
      const normalizedBestMonths = bestMonths
        .map((month) => {
          if (typeof month === 'number') return month;
          if (typeof month === 'string') {
            const index = monthNames.findIndex((m) =>
              month.toLowerCase().includes(m.toLowerCase())
            );
            return index !== -1 ? index + 1 : null;
          }
          return null;
        })
        .filter((m) => m !== null);

      if (normalizedBestMonths.length > 0) {
        const sortedMonths = [...normalizedBestMonths].sort((a, b) => a - b);
        const nextMonthIndex = sortedMonths.findIndex(
          (month) => month > currentMonth
        );
        let nextMonth;

        if (nextMonthIndex !== -1) {
          nextMonth = sortedMonths[nextMonthIndex];
        } else {
          nextMonth = sortedMonths[0];
        }

        return `Available in ${monthNames[nextMonth - 1]}`;
      }
    }

    // Parse harvest months ranges (e.g., "December-April (Northern Africa)")
    if (harvestMonths && harvestMonths.length > 0) {
      for (const harvestRange of harvestMonths) {
        const monthMatch = harvestRange.match(/([A-Za-z]+)-([A-Za-z]+)/);
        if (monthMatch) {
          return `Next harvest: ${monthMatch[1]} - ${monthMatch[2]}`;
        }
      }
      return `Next harvest: ${harvestMonths[0]}`;
    }

    // Find next peak season
    if (peak && peak.length > 0) {
      const seasonOrder = ['Spring', 'Summer', 'Fall', 'Winter'];
      const currentSeason = this.getCurrentSeason();
      const currentSeasonIndex = seasonOrder.indexOf(currentSeason);

      for (let i = 1; i <= 4; i++) {
        const nextSeasonIndex = (currentSeasonIndex + i) % 4;
        const nextSeason = seasonOrder[nextSeasonIndex];
        if (peak.some((p) => p.toLowerCase() === nextSeason.toLowerCase())) {
          return `Available in ${nextSeason}`;
        }
      }
    }

    // Check available range
    if (available && typeof available === 'string' && available.includes('-')) {
      return `Available ${available}`;
    }

    // Default message
    if (available && available !== 'Year-round') {
      return `Available: ${available}`;
    }

    return 'Contact us for availability';
  },

  // Get detailed season information for display
  getSeasonDetails(product) {
    if (!product?.seasons) {
      return {
        type: 'year-round',
        status: 'Available',
        description: 'Product is available throughout the year',
        inSeason: true,
        isAvailable: true,
      };
    }

    const { peak, available, bestMonths, harvestMonths } = product.seasons;
    const seasonStatus = this.getSeasonStatus(product);

    let description = '';

    // Build description based on season status
    if (seasonStatus.inSeason) {
      if (peak && peak.length > 0) {
        description = `Peak season: ${peak.join(', ')}`;
      } else if (bestMonths && bestMonths.length > 0) {
        // Handle both string and number months
        const bestMonthNames = bestMonths.map((month) => {
          if (typeof month === 'number') {
            const monthNames = [
              'January',
              'February',
              'March',
              'April',
              'May',
              'June',
              'July',
              'August',
              'September',
              'October',
              'November',
              'December',
            ];
            return monthNames[month - 1];
          }
          return month;
        });
        description = `Best quality: ${bestMonthNames.join(', ')}`;
      } else if (available && !available.includes('-')) {
        description = `Available: ${available}`;
      } else if (available && available.includes('-')) {
        description = `${available}`;
      } else {
        description = 'Currently in season';
      }
    } else {
      if (seasonStatus.nextAvailable) {
        description = seasonStatus.nextAvailable;
      } else if (harvestMonths && harvestMonths.length > 0) {
        description = `Next harvest: ${harvestMonths.join(', ')}`;
      } else if (bestMonths && bestMonths.length > 0) {
        const nextMonth = bestMonths[0];
        if (typeof nextMonth === 'number') {
          const monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ];
          description = `Next available: ${monthNames[nextMonth - 1]}`;
        } else {
          description = `Next available: ${nextMonth}`;
        }
      } else if (available && available.includes('-')) {
        description = `Available ${available}`;
      } else {
        description = 'Currently out of season - contact for availability';
      }
    }

    // return a complete object
    return {
      type: seasonStatus.type,
      status: seasonStatus.status,
      description: description,
      inSeason: seasonStatus.inSeason,
      isAvailable: seasonStatus.inSeason,
      peakSeasons: peak,
      availableMonths: available,
      bestMonths: bestMonths,
      harvestMonths: harvestMonths || [],
    };
  },
};
